import React, { FC } from "react";

import { Badge, Picto } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { TView } from "@/types";

interface IViewBannerProps {
	view: Pick<
		TView,
		| "name"
		| "color"
		| "description"
		| "createDate"
		| "isArchived"
		| "isEverything"
	>;
}

/**
 * The view's calling card under the page title: its colour as a tile,
 * its description and creation date, and what it selects (everything it
 * reaches, or a picked set) plus the lock flag.
 */
export const ViewBanner: FC<IViewBannerProps> = ({ view }) => (
	<div
		className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-4"
		data-testid="view-banner"
	>
		<span
			className="h-12 w-12 shrink-0 rounded-xl"
			style={{ backgroundColor: view.color }}
			aria-hidden
		/>
		<div className="flex min-w-0 grow flex-col gap-1">
			<span className="flex flex-wrap items-center gap-2">
				<span className="truncate text-lg font-medium">
					{view.name}
				</span>
				{view.isArchived && (
					<Badge
						size="sm"
						variant="tint"
						color="neutral"
						picto="archive"
					>
						{t("views.archived")}
					</Badge>
				)}
				{view.isEverything && (
					<Badge size="sm" variant="tint" color="info" picto="eye">
						{t("views.everything")}
					</Badge>
				)}
			</span>
			<span className="flex flex-wrap items-center gap-1 text-sm text-neutral-500">
				{view.description && <span>{view.description} ·</span>}
				<span className="flex items-center gap-1">
					<Picto icon="calendar" className="h-3.5 w-3.5" />
					{t("views.createdOn", {
						date: format(new Date(view.createDate), "d MMM yyyy"),
					})}
				</span>
			</span>
		</div>
	</div>
);
